"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ICONS } from "@artha/icons";
import type { JobItemDto } from "@artha/api-contracts";
import type { CreatorProfileForMyLink } from "@artha/types";
import { Button, JobCardPublic, ProfileAvatar, Typography, formatLocation } from "@artha/ui";
import { usePublicJobAlertSubscription, JobAlertSubscriptionCard } from "@/modules/job-alerts";
import { authService } from "@/lib/authService";

import { PageLayout } from "@/components/layout/PageLayout";
import { InstagramLoader } from "@/components/common/InstagramLoader";
import {
  formatExperience,
  isRphJob,
  sanitizeDescription,
} from "@/components/jobs/jobDetailsUtils";
import { RphExperienceNotice } from "@/components/jobs/RphExperienceNotice";
import {
  type ApplyClickPortalContext,
  useApplyClick,
} from "@/components/jobs/useApplyClick";
import {
  CountryRestrictionModal,
  VpnRestrictionModal,
  IncognitoRestrictionModal,
  useApplyRestrictions,
} from "@/modules/geolocation";
import { ShareJobBottomSheet } from "@/components/share/ShareJobBottomSheet";
import { useShareJob } from "@/components/share/useShareJob";
import {
  capitalizeFirst,
  formatSalary,
  generateJobShareUrl,
  generateEarnCtaUrl,
  normalizeUsernameParam,
  shouldHideSalaryBadge,
} from "@/lib/utils";
import {
  useAudienceAnalytics,
  AudienceAnalyticsProvider,
  buildAudienceJobProps,
  getLastSeenPulseVariant,
} from "@/lib/analytics";
import { fetchSimilarJobs } from "@/modules/common/services/jobApi";
import {
  ResumePersonalizationSheet,
  useCandidatePersonalization,
  useResumeEligibilityApply,
} from "@/modules/candidate-personalization";

type JobDetailsPageClientProps = {
  username: string;
  job: JobItemDto;
  creator?: CreatorProfileForMyLink | null;
  creatorAvatarUrl?: string | null;
  creatorName?: string | null;
  creatorId?: string | null;
  referralInviteCode?: string | null;
  hidePoweredBy?: boolean;
};

function JobDetailsPageClientInner({
  username,
  job,
  creator,
  creatorAvatarUrl,
  creatorName,
  creatorId,
  referralInviteCode,
  hidePoweredBy,
}: JobDetailsPageClientProps) {
  const router = useRouter();
  const t = useTranslations("jobDetails");
  const jobAlertSubscription = usePublicJobAlertSubscription(username);
  const profilePath = useMemo(() => {
    if (!username) return "/";
    const normalized = normalizeUsernameParam(username);
    return normalized ? `/@${normalized}` : "/";
  }, [username]);
  const searchPath = `${profilePath}/search`;
  const jobDetailsPath = `${profilePath}/jobs`;
  const jobSlug = job.slug || "";
  const { trackJobView, trackJobApply, trackAudEarnClick } = useAudienceAnalytics();
  const trackedJobViewRef = useRef<string | null>(null);
  const applyPortalContext = useMemo<ApplyClickPortalContext | undefined>(
    () =>
      creatorId
        ? {
          portalId: creatorId,
          clientName: creatorName || username,
          domainName: normalizeUsernameParam(username),
        }
        : undefined,
    [creatorId, creatorName, username],
  );
  const { trackAndRedirect } = useApplyClick(applyPortalContext);
  const [similarJobs, setSimilarJobs] = useState<JobItemDto[]>([]);
  const [isSimilarLoading, setIsSimilarLoading] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const {
    restriction,
    isCountryModalOpen,
    closeCountryModal,
    isVpnModalOpen,
    closeVpnModal,
    isIncognitoModalOpen,
    closeIncognitoModal,
    blockIfRestricted,
  } = useApplyRestrictions({
    job,
    creatorUsername: username,
    creator: creator || { id: creatorId, displayName: creatorName, username },
  });

  useEffect(() => {
    setIsAuthenticated(authService.isAuthenticated());
  }, []);

  useEffect(() => {
    const loadSimilar = async () => {
      if (!jobSlug) {
        setSimilarJobs([]);
        return;
      }
      try {
        setIsSimilarLoading(true);
        const res = await fetchSimilarJobs(jobSlug, 10, undefined, "US", {
          portalId: creatorId || undefined,
        });
        setSimilarJobs(res.items.slice(0, 5));
      } catch {
        setSimilarJobs([]);
      } finally {
        setIsSimilarLoading(false);
      }
    };
    loadSimilar();
  }, [jobSlug, creatorId]);

  useEffect(() => {
    if (!job.id || trackedJobViewRef.current === job.id) return;
    if (trackJobView(job)) trackedJobViewRef.current = job.id;
  }, [job, trackJobView]);

  const skills = job?.skills?.length ? job.skills : (job?.keywords ?? []);
  const [descriptionHtml, setDescriptionHtml] = useState("");
  const locationLabel = formatLocation({
    city: job.city,
    state: job.state,
    country: job.country,
    location: job.location,
    work_mode: job.work_mode,
  });
  const shareUrl = useMemo(() => {
    return generateJobShareUrl(username, jobSlug);
  }, [jobSlug, username]);
  const shareMessage = useMemo(
    () =>
      t("shareMessage", {
        jobTitle: job.title ?? "",
        company: job.company ?? "",
        location: locationLabel,
        jobUrl: shareUrl,
      }),
    [job.company, job.title, locationLabel, shareUrl, t],
  );
  const {
    isOpen,
    shareUrl: activeShareUrl,
    openShare,
    closeShare,
  } = useShareJob({
    title: job.title,
    text: shareMessage,
    url: shareUrl,
  });

  useEffect(() => {
    setDescriptionHtml(sanitizeDescription(job?.description));
  }, [job?.description]);

  const normalizedUsername = useMemo(
    () => normalizeUsernameParam(username),
    [username],
  );
  const jobDetailsPageType = "creator_job_details";
  const pageViewJobProps = useMemo(() => buildAudienceJobProps(job), [job]);
  const pulseVariant = getLastSeenPulseVariant();
  const earnHref = generateEarnCtaUrl(referralInviteCode);
  const personalization = useCandidatePersonalization(creatorId || undefined);

  const handleApplyRedirect = useCallback(
    (clickAttemptId?: string) => {
      trackJobApply(job, undefined, clickAttemptId);
    },
    [job, trackJobApply],
  );
  const resumeEligibility = useResumeEligibilityApply({
    job,
    trackAndRedirect,
    onRedirect: handleApplyRedirect,
  });

  const handleEarnClick = useCallback(() => {
    trackAudEarnClick(normalizedUsername);
  }, [normalizedUsername, trackAudEarnClick]);

  const handleApply = async () => {
    if (isApplying) return;
    if (blockIfRestricted()) return;

    setIsApplying(true);
    try {
      await resumeEligibility.apply();
    } finally {
      setIsApplying(false);
    }
  };

  const handleShowSimilarJobs = () => {
    closeCountryModal();
    router.push(profilePath);
  };

  return (
    <PageLayout className="relative bg-[#FFF8E8] overflow-x-hidden">
      <main
        id="creator-job-details-page"
        data-experiment-id="creator-job-details-page"
        className="creator-job-details-page flex flex-col items-center gap-6 px-4 pt-6 pb-16"
        data-posthog-pageview="true"
        data-posthog-page-name={jobDetailsPageType}
        data-posthog-page-type={jobDetailsPageType}
        data-posthog-creator-id={creatorId ?? undefined}
        data-posthog-creator-username={normalizedUsername}
        data-posthog-username={normalizedUsername}
        data-posthog-job-id={pageViewJobProps.job_id}
        data-posthog-job-title={pageViewJobProps.job_title}
        data-posthog-job-company={pageViewJobProps.job_company}
        data-posthog-job-value={
          typeof pageViewJobProps.job_value === "number"
            ? String(pageViewJobProps.job_value)
            : undefined
        }
        data-posthog-revenue-type={pageViewJobProps.revenue_type}
        data-posthog-pulse-variant={pulseVariant ?? undefined}
      >
        <section
          id="creator-job-details-section"
          data-experiment-id="creator-job-details-section"
          className="creator-job-details-section w-full space-y-6"
        >
          <div className="sticky top-0 z-30 -mx-4 px-4 py-3 bg-[#FFF8E8]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1 min-w-0">
                <Link
                  href={profilePath}
                  className="rounded-md p-1 text-[#344054]"
                  aria-label="Back to profile"
                >
                  <ICONS.ARROW_LEFT className="h-5 w-5 text-[#344054]" />
                </Link>
                <div className="flex items-center gap-2 min-w-0">
                  <ProfileAvatar
                    src={creatorAvatarUrl}
                    alt={creatorName || username}
                    className="h-8 w-8 shrink-0 rounded-full border border-black"
                    fallbackClassName="bg-white"
                    logoClassName="h-[54%] w-[54%]"
                  />
                  <Typography
                    variant="small"
                    className="text-[#344054] text-[14px] font-medium truncate"
                  >
                    {creatorName || username}
                  </Typography>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2">
                <div className="h-9 w-9 min-h-[36px] min-w-[36px] rounded-md overflow-hidden bg-white border border-[#E4E7EC] flex items-center justify-center text-sm font-semibold text-[#475467]">
                  {job.logo ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={job.logo}
                      alt={job.company || "Company logo"}
                      className="block h-full w-full object-contain"
                    />
                  ) : (
                    (job.company?.charAt(0) ?? "?")
                  )}
                </div>
                <Typography
                  variant="small"
                  className="text-[#667085] text-[14px] leading-[18px] whitespace-normal"
                >
                  {job.company}
                </Typography>
              </div>

              <div className="flex flex-col gap-2">
                <Typography
                  variant="lead"
                  as="h1"
                  className="text-[#344054] text-[16px] font-semibold leading-tight break-words"
                >
                  {job.title}
                </Typography>
                <div className="flex items-center gap-1.5 text-[14px] text-[#667085]">
                  <ICONS.LOCATION_PIN className="h-3 w-3 text-[#667085]" />
                  <span className="truncate max-w-[260px]">
                    {locationLabel}
                  </span>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 text-[14px] text-[#475467]">
                <MetaBadge
                  icon={<ICONS.OFFICE_BAG className="h-5 w-5 text-[#98A2B3]" />}
                  label={formatExperience(job)}
                />
                <MetaBadge
                  icon={<ICONS.CLOCK className="h-5 w-5 text-[#98A2B3]" />}
                  label={capitalizeFirst(job.job_type)}
                />
                <MetaBadge
                  icon={<ICONS.FRAME className="h-5 w-5 text-[#98A2B3]" />}
                  label={formatSalary(job)}
                />
              </div>

              {skills.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {skills.slice(0, 6).map((skill) => (
                    <span
                      key={skill}
                      className="rounded-[4px] border border-[#1877F2] bg-[#E6F9FD] px-2 py-0.5 text-[12px] text-[#1877F2] font-medium"
                    >
                      {skill}
                    </span>
                  ))}
                  {skills.length > 6 && (
                    <span className="rounded-[4px] border border-[#1877F2] bg-[#E6F9FD] px-2 py-0.5 text-[12px] text-[#1877F2] font-medium">
                      +{skills.length - 6}
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="border-t border-[#D1D1D6]" />

            <div className="space-y-4">
              {isRphJob(job) && <RphExperienceNotice />}
              <Typography
                variant="lead"
                as="h2"
                className="text-[#344054] text-[16px] font-medium"
              >
                {t("aboutJobTitle")}
              </Typography>

              {descriptionHtml ? (
                <div
                  className="w-full break-words space-y-3 text-[14px] leading-[20px] text-[#667085] font-plus-jakarta-sans [&_p]:text-[#667085] [&_p]:leading-[20px] [&_p]:mt-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1"
                  dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                />
              ) : (
                <Typography
                  variant="lead"
                  className="text-[#667085] text-[14px] font-plus-jakarta-sans"
                >
                  {t("descriptionFallback")}
                </Typography>
              )}
            </div>

            <div className="border-t border-[#D1D1D6]" />

            {(!jobAlertSubscription.externalId || jobAlertSubscription.isModalOpen) && (
              <>
                <JobAlertSubscriptionCard
                  cardId="page-detail-alert"
                  controller={jobAlertSubscription}
                  creatorName={creatorName ?? undefined}
                />
                <div className="border-t border-[#D1D1D6]" />
              </>
            )}

            {(isSimilarLoading || similarJobs.length > 0) && (
              <div
                id="creator-similar-jobs-section"
                data-experiment-id="creator-similar-jobs-section"
                className="creator-similar-jobs-section space-y-4"
              >
                <Typography
                  variant="lead"
                  className="text-[#344054] text-[16px] font-medium leading-none overflow-hidden text-ellipsis whitespace-nowrap"
                >
                  {t("similarJobsTitle")}
                </Typography>
                {isSimilarLoading && (
                  <InstagramLoader label={t("loadingSimilarJobs")} />
                )}

                {!isSimilarLoading && similarJobs.length > 0 && (
                  <div className="flex gap-3 overflow-x-auto pb-2 items-stretch pr-2">
                    {similarJobs.map((similar, index) => {
                      const similarSlug = similar.slug;
                      const similarPath = similarSlug
                        ? `${jobDetailsPath}/${encodeURIComponent(similarSlug)}`
                        : "";
                      const card = (
                        <JobCardPublic
                          jobTitle={similar.title}
                          companyName={similar.company}
                          companyLogo={similar.logo || ""}
                          location={similar.location || ""}
                          city={similar.city}
                          state={similar.state}
                          country={similar.country}
                          salary={formatSalary(similar)}
                          experience=""
                          workMode={similar.job_type ?? ""}
                          skills={
                            similar.skills?.length
                              ? similar.skills
                              : similar.keywords
                          }
                          hideSalary={shouldHideSalaryBadge(similar)}
                          cpaValue={isAuthenticated ? similar.cpa_value : undefined}
                          cpcValue={isAuthenticated ? similar.cpc_value : undefined}
                          className="h-full"
                        />
                      );
                      if (similarPath) {
                        return (
                          <Link
                            key={`${similar.ref_id || similar.id || similar.url || "similar"}`}
                            id={`creator-job-details-similar-job-card-${index}`}
                            data-experiment-id="creator-job-card"
                            href={similarPath}
                            className="creator-job-card-link min-w-[90%] max-w-[90%] shrink-0 text-left flex sm:min-w-[320px] sm:max-w-[360px]"
                          >
                            {card}
                          </Link>
                        );
                      }
                      return (
                        <a
                          key={`${similar.ref_id || similar.id || similar.url || "similar"}`}
                          id={`creator-job-details-similar-job-card-${index}`}
                          data-experiment-id="creator-job-card"
                          href={similar.url || "#"}
                          className="creator-job-card-link min-w-[90%] max-w-[90%] shrink-0 text-left flex sm:min-w-[320px] sm:max-w-[360px]"
                          onClick={(event) => {
                            event.preventDefault();
                            if (similar.url) {
                              window.open(similar.url, "_blank");
                            }
                          }}
                        >
                          {card}
                        </a>
                      );
                    })}
                  </div>
                )}

                {!isSimilarLoading && similarJobs.length > 0 && (
                  <Button
                    asChild
                    variant="outline"
                    className="creator-job-card-link w-full h-12 border border-black bg-transparent text-[#667085] font-medium"
                  >
                    <Link
                      id="creator-job-details-see-more-jobs-link"
                      data-experiment-id="creator-see-more-jobs-link"
                      href={`${searchPath}?slug=${encodeURIComponent(jobSlug)}`}
                    >
                      {t("seeMoreJobs")}
                    </Link>
                  </Button>
                )}
              </div>
            )}

            {!hidePoweredBy && (
              <Button
                asChild
                variant="outline"
                className="creator-earn-cta mt-2 w-full gap-2 rounded-[8px] border border-black bg-white px-3 py-5 text-[16px] font-medium shadow-[0px_4px_0px_0px_#000000]"
              >
                <a
                  id="creator-job-details-earn-cta"
                  data-experiment-id="creator-earn-cta"
                  href={earnHref}
                  onClick={handleEarnClick}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ICONS.ARTHA_LINK_BADGE className="h-5 w-5" />
                  {t("earnCta")}
                </a>
              </Button>
            )}
            <div className="w-full" aria-hidden="true" />
          </div>
        </section>

        <ShareJobBottomSheet
          isOpen={isOpen}
          onClose={closeShare}
          shareUrl={activeShareUrl}
          shareText={shareMessage}
          jobTitle={job.title}
          companyName={job.company}
          companyLogoUrl={job.logo}
          creatorName={creatorName || username}
          creatorAvatarUrl={creatorAvatarUrl}
          creatorUsername={normalizeUsernameParam(username)}
        />
        <CountryRestrictionModal
          isOpen={isCountryModalOpen}
          onClose={closeCountryModal}
          title={t("countryRestrictionTitle", {
            jobCountry: restriction.jobCountry?.name || job.country || "",
          })}
          subtitle={t("countryRestrictionSub", {
            seekerCountry: restriction.seekerCountry?.name || "",
          })}
          actionLabel={t("countryRestrictionViewSimilarJobs")}
          onShowSimilarJobs={handleShowSimilarJobs}
        />
        <VpnRestrictionModal
          isOpen={isVpnModalOpen}
          onClose={closeVpnModal}
        />
        <IncognitoRestrictionModal
          isOpen={isIncognitoModalOpen}
          onClose={closeIncognitoModal}
        />
        <ResumePersonalizationSheet
          isOpen={resumeEligibility.isOpen}
          onClose={() => resumeEligibility.setIsOpen(false)}
          onComplete={resumeEligibility.complete}
          controller={personalization}
          mode="apply_eligibility"
          job={job}
          skipFitCheck={resumeEligibility.skipFitCheck}
          onViewJobs={() => {
            resumeEligibility.setIsOpen(false);
            router.push(profilePath);
          }}
        />
      </main>

      <div
        id="creator-job-details-floating-actions"
        data-experiment-id="creator-floating-actions"
        className="creator-floating-actions fixed bottom-6 left-1/2 z-40 w-full max-w-[437px] -translate-x-1/2 px-4 pointer-events-none"
      >
        <div className="w-full pointer-events-auto">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              id="creator-job-details-share-trigger"
              data-experiment-id="creator-share-job-trigger"
              className="rounded-[8px] border-black bg-white p-[10px] shadow-[0px_4px_0px_0px_#000000] cursor-pointer"
              aria-label="Share job"
              onClick={openShare}
            >
              <ICONS.SHARE className="h-5 w-5 text-[#101828]" />
            </Button>
            <Button
              id="creator-job-details-apply-job-trigger"
              data-experiment-id="creator-apply-job-trigger"
              className="flex-1 shadow-[0px_4px_0px_0px_#000000] bg-[#0D41E1] text-white border-black text-[16px] font-medium"
              onClick={() => {
                void handleApply();
              }}
              disabled={isApplying}
            >
              <span className="inline-flex items-center gap-2">
                {isApplying ? (
                  <span className="h-4 w-4 rounded-full border-2 border-white/70 border-t-transparent animate-spin" />
                ) : null}
                {isApplying ? "Applying..." : t("applyNow")}
              </span>
            </Button>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}

const MetaBadge = ({
  icon,
  label,
}: {
  icon: React.ReactNode;
  label?: string;
}) => {
  if (!label) return null;
  return (
    <span className="inline-flex items-center gap-2 text-[14px] text-[#475467]">
      {icon}
      {label}
    </span>
  );
};

export default function JobDetailsPageClient(props: JobDetailsPageClientProps) {
  return (
    <AudienceAnalyticsProvider
      username={props.username}
      creatorId={props.creatorId || undefined}
    >
      <JobDetailsPageClientInner {...props} />
    </AudienceAnalyticsProvider>
  );
}
